
import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { GlobalStyles } from "@mui/material";
import {
  Drawer,
  IconButton,
  Divider,
  Typography,
  Box,
  Checkbox,
  Button,
  FormControlLabel,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import dayjs, { Dayjs } from "dayjs";
import CreatedTimeFilter from "../../admin-section/features/provider-management/provider-requests/provider-requests-filter-options/created-timer-filter";
import "./preneed-filter.scss";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

export type FilterValues = {
  status: Array<
    "confirmed" | "completed" | "cancelled" | "pending"
  >;
  fromDate?: string;
  toDate?: string;
  appointmentDate?: string; // single date
};


type FilterProps = {
  open: boolean;
  onClose: () => void;
  onApply: (values: FilterValues) => void;
  initialValues?: Partial<FilterValues>;
  onReset?: () => void;
  serviceCategoryOptions?: string[];
};

const DEFAULT_VALUES: FilterValues = {
  status: [],
  fromDate: "",
  toDate: "",
  appointmentDate: "",
};

export default function PreneedFilter({
  open,
  onClose,
  onApply,
  initialValues,
  onReset,
}: FilterProps) {

  const [fromDate, setCreatedFrom] = useState<Dayjs | null>(null);
  const [toDate, setCreatedTo] = useState<Dayjs | null>(null);
  const [appointmentDate, setAppointmentDate] = useState<Dayjs | null>(null);
  const [appointmentError, setAppointmentError] = useState<string | null>(null);
  const [fromDatePickerError, setFromDatePickerError] = useState<string | null>(null);
  const [toDatePickerError, setToDatePickerError] = useState<string | null>(null);

  const formikValidate = useCallback((vals: FilterValues) => {
    const errors: Partial<Record<keyof FilterValues, string>> = {};
    if (vals.fromDate || vals.toDate) {
      if (!vals.fromDate) {
        errors.fromDate = "Start date is required";
      }

      if (!vals.toDate) {
        errors.toDate = "End date is required";
      }

      if (vals.fromDate && vals.toDate) {
        const startDate = dayjs(vals.fromDate);
        const endDate = dayjs(vals.toDate);

        if (!startDate.isValid()) {
          errors.fromDate = "Invalid start date";
        }

        if (!endDate.isValid()) {
          errors.toDate = "Invalid end date";
        }

        if (startDate.isAfter(endDate)) {
          errors.fromDate = "Start date cannot be after end date";
          errors.toDate = "End date cannot be before start date";
        }
      }
    }
    if (vals.appointmentDate) {
      const date = dayjs(vals.appointmentDate);

      if (!date.isValid()) {
        errors.appointmentDate = "Invalid appointment date";
      }
    }

    return errors;
  }, []);


  const formik = useFormik<FilterValues>({
    initialValues: DEFAULT_VALUES,
    validate: formikValidate,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (vals) => {
      const payload: FilterValues = {
        ...vals,
      };
      onApply(payload);
      onClose();
    },
  });

  useEffect(() => {
    if (!open) return;
    setAppointmentError(null);
    setFromDatePickerError(null);
    setToDatePickerError(null);
    const nextValues: FilterValues = {
      ...DEFAULT_VALUES,
      ...initialValues,
      status: initialValues?.status ?? [],
      fromDate: initialValues?.fromDate ?? "",
      toDate: initialValues?.toDate ?? "",
    };
    formik.resetForm({ values: nextValues });
    setCreatedFrom(
      nextValues.fromDate ? dayjs(nextValues.fromDate) : null,
    );
    const appointment = nextValues.appointmentDate
      ? dayjs(nextValues.appointmentDate)
      : null;

    setAppointmentDate(appointment && appointment.isValid() ? appointment : null);

    setCreatedTo(nextValues.toDate ? dayjs(nextValues.toDate) : null);
  }, [open, initialValues]);

  const handleReset = () => {
    const resetValues = DEFAULT_VALUES;

    formik.resetForm({ values: resetValues });

    setCreatedFrom(null);
    setCreatedTo(null);
    setAppointmentError(null);
    setAppointmentDate(null);
    setFromDatePickerError(null);
    setToDatePickerError(null);

    onApply(resetValues);   // clear filters in parent
    onReset?.();            // optional extra reset
  };

  const handleCreatedDateRange = useCallback(
    (from: Date | null, to: Date | null) => {
      const fromDayjs = from ? dayjs(from) : null;
      const toDayjs = to ? dayjs(to) : null;
      setCreatedFrom(fromDayjs);
      setCreatedTo(toDayjs);
      const isFromValid = fromDayjs && fromDayjs.isValid();
      const isToValid = toDayjs && toDayjs.isValid();

      formik.setFieldValue(
        "fromDate",
        isFromValid ? fromDayjs.toISOString() : "",
        false,
      );

      formik.setFieldValue(
        "toDate",
        isToValid ? toDayjs.toISOString() : "",
        false,
      );
      formik.setFieldTouched("fromDate", true, false);
      formik.setFieldTouched("toDate", true, false);
    },
    [formik],
  );


const hasAnyFilterApplied = useMemo(() => {
  const v = formik.values;

  return (
    v.status.length > 0 ||
    Boolean(v.fromDate) ||
    Boolean(v.toDate) ||
    Boolean(v.appointmentDate)
  );
}, [formik.values]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ className: "preneed-management-filter__paper" }}
    >
      <GlobalStyles
        styles={{
          ".MuiAutocomplete-popper .MuiPaper-root": {
            padding: "0 !important",
          },
          ".MuiAutocomplete-popper .MuiAutocomplete-listbox": {
            padding: "0 !important",
            margin: "0 !important",
            maxHeight: "unset !important",
            height: "auto !important",
          },
          ".MuiAutocomplete-popper .MuiAutocomplete-noOptions": {
            padding: "6px 12px !important",
            minHeight: "unset !important",
            lineHeight: "1.2 !important",
          },
        }}
      />
      <div className="preneed-management-filter">
        <div className="filterDrawer__header">
          <Typography
            variant="h6"
            className="filterDrawer__title"
            sx={{ fontWeight: 600 }}
          >
            Filter
          </Typography>
          <IconButton
            onClick={onClose}
            className="filterDrawer__closeBtn"
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </div>
        <Divider />
        <div className="filterDrawer__body">
          <div className="filterDrawer__field">
            <Typography className="filterDrawer__label">Date Range</Typography>

            <CreatedTimeFilter
              value="Custom"
              onChange={() => { }}
              isCreatedTimeEnabled={true}
              onDateRangeChange={handleCreatedDateRange}
              createdTimeError={
                formik.errors.fromDate || formik.errors.toDate || ""
              }
              createdTimeStartError={fromDatePickerError || formik.errors.fromDate || ""}
              createdTimeEndError={toDatePickerError || formik.errors.toDate || ""}
              setCreatedTimeFromDate={setCreatedFrom}
              setCretedTimeToDate={setCreatedTo}
              showDropdown={false}
              fromDate={fromDate}
              toDate={toDate}
              onFromDateError={setFromDatePickerError}
              onToDateError={setToDatePickerError}
            />
          </div>

          <div className="filterDrawer__field">
            <Typography className="filterDrawer__label">
              Appointment Date
            </Typography>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={appointmentDate}
                onChange={(newValue) => {
                  setAppointmentDate(newValue);

                  if (newValue && newValue.isValid()) {
                    formik.setFieldValue(
                      "appointmentDate",
                      newValue.startOf("day").toISOString()
                    );
                  } else {
                    formik.setFieldValue("appointmentDate", "");
                  }
                }}
                onError={(reason) => {
                  if (reason) {
                    setAppointmentError("Invalid appointment date");
                    formik.setFieldError("appointmentDate", "Invalid appointment date");
                  } else {
                    setAppointmentError(null);
                    formik.setFieldError("appointmentDate", "");
                  }
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: Boolean(appointmentError),
                    helperText: appointmentError || "",
                    inputProps: {
                      placeholder: "Enter date",
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </div>
          <div className="filterDrawer__field">
            <Typography className="filterDrawer__label">Status</Typography>

            <div className="filterDrawer__statusColumn">
              {(["confirmed", "cancelled", "pending", "completed"] as const).map(
                (s) => {
                  const checked = formik.values.status.includes(s);

                  return (
                    <FormControlLabel
                      key={s}
                      control={
                        <Checkbox
                          size="small"
                          checked={checked}
                          onChange={(e) => {
                            const isChecked = e.target.checked;

                            const next = isChecked
                              ? [...formik.values.status, s]
                              : formik.values.status.filter((x) => x !== s);

                            formik.setFieldValue("status", next);
                          }}
                        />
                      }
                      label={s.charAt(0).toUpperCase() + s.slice(1)}
                    />
                  );
                },
              )}
            </div>

            {formik.errors.status && (
              <FormHelperText error>{formik.errors.status}</FormHelperText>
            )}
          </div>
        </div>
        {hasAnyFilterApplied && (
          <>
            <Divider />
            <div className="filterDrawer__footer">
              <Button
                variant="outlined"
                onClick={handleReset}
                className="filterDrawer__btn filterDrawer__btn--cancel"
                sx={{
                  backgroundColor: "transparent",
                  color: "black",
                  border: "1px solid #9e9e9e",
                  boxShadow: "none",
                  "&:focus": {
                    outline: "none",
                  },
                }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                sx={{ backgroundColor: "#EBD28D", color: "black" }}
                onClick={() => formik.handleSubmit()}
                className="filterDrawer__btn filterDrawer__btn--apply"
                disabled={!formik.isValid || !!appointmentError || !!fromDatePickerError || !!toDatePickerError}
              >
                Apply
              </Button>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}
